import { Reveal } from "@/components/site/Reveal";
import { PageHeader } from "@/components/site/PageParts";
import { SITE } from "@/siteData";
import { useMeta } from "@/useMeta";

const P = ({ children }) => (
  <Reveal>
    <p className="text-base leading-[1.8] text-ink-soft">{children}</p>
  </Reveal>
);

const H = ({ children }) => (
  <Reveal>
    <h2 className="!mt-12 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">{children}</h2>
  </Reveal>
);

const List = ({ items }) => (
  <Reveal>
    <ul className="mt-2 space-y-2">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-base leading-relaxed text-ink-soft">
          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brand" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </Reveal>
);

export default function Privacy() {
  useMeta({
    title: "Privacy Policy | Simple Background Remover",
    description:
      "Privacy Policy for Simple Background Remover by Hildreth Technologies LLC. Photos are processed locally on your device and are never uploaded, collected, or stored.",
  });

  return (
    <>
      <PageHeader
        index="/privacy"
        eyebrow="Legal"
        title="Privacy Policy"
        effective={SITE.effectiveDate}
      />
      <div className="container-x max-w-3xl py-16 sm:py-20">
        <div className="space-y-6">
          <P>Simple Background Remover is provided by Hildreth Technologies LLC.</P>
          <P>Simple Background Remover processes selected photographs locally on the user’s device.</P>
          <P>
            Photographs are not uploaded to Hildreth Technologies LLC, a cloud server, an
            image-processing website, or another remote image-processing provider.
          </P>
          <P>The application does not operate a customer-photo server or cloud photo library.</P>
          <P>
            The application uses the operating system’s photo picker. Users choose the specific
            photographs they want processed.
          </P>
          <P>
            Foreground detection, background removal, and white or black background replacement occur
            locally on the device.
          </P>
          <P>
            Purchases and subscriptions are processed by Google Play or Apple. Hildreth
            Technologies LLC does not receive or store full payment-card information. The app
            receives subscription product, purchase status, renewal status, and expiration
            information needed to provide paid access. Restoring purchases contacts the applicable
            app store.
          </P>
          <P>
            On supported Android devices, Google Play services may download an on-device
            background-removal model. The model is installed locally. Hildreth Technologies LLC does
            not upload selected photographs during this model download.
          </P>
          <P>
            The application may temporarily create local working copies, masks, thumbnails, and
            completed images inside private app storage while processing a batch.
          </P>

          <H>Temporary files are designed to be deleted when:</H>
          <List
            items={[
              "The user saves the completed batch",
              "The user clears the batch",
              "The user begins a new batch",
              "The application cleans up an interrupted session",
              "The application is uninstalled",
            ]}
          />

          <P>
            Images deliberately saved by the user remain in the device’s Photos application or gallery
            until the user deletes them.
          </P>
          <P>Simple Background Remover does not require an account.</P>

          <H>Hildreth Technologies LLC does not use the application to collect:</H>
          <List
            items={[
              "Names",
              "Email addresses",
              "Telephone numbers",
              "Mailing addresses",
              "Precise location",
              "Contacts",
              "Payment-card information",
              "Customer photographs",
              "Facial-recognition data",
              "Advertising identifiers",
              "Browsing history",
              "App activity for advertising or profiling",
            ]}
          />

          <P>
            The application does not contain advertising SDKs, behavioral analytics, attribution
            tracking, session replay, or cross-app tracking.
          </P>
          <P>Hildreth Technologies LLC does not sell user information.</P>
          <P>
            Photo-library access is used only to select photographs and save completed photographs.
          </P>
          <P>The application does not alter or delete original photographs.</P>
          <P>
            Hildreth Technologies LLC does not share customer photographs or personal information with
            advertisers or data brokers.
          </P>
          <P>
            Simple Background Remover is a general-purpose utility and is not directed toward children
            under 13. The application does not knowingly collect personal information from children.
          </P>
          <P>
            Automatic background removal may not perfectly identify every object or edge. Users should
            review completed images before publishing them.
          </P>
          <P>
            Hildreth Technologies LLC may update this Privacy Policy when functionality, legal
            requirements, or data practices change.
          </P>

          <H>Contact</H>
          <Reveal>
            <div className="mt-2 rounded-2xl border border-line bg-paper-2 p-6">
              <a
                href={`mailto:${SITE.email}`}
                className="link-underline font-mono text-base text-brand"
                data-testid="privacy-email"
              >
                {SITE.email}
              </a>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">
                Hildreth Technologies LLC
                <br />
                {SITE.location}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
